import { useCallback, useEffect, useState } from "react";
import { createSshKey, deleteSshKey, listSshKeys } from "@/api/sshKeys";
import { useAuthState } from "@/store/auth";
import { CreateSshKeyInput, SavedSshKey } from "@/types/savedSshKey";
import { getToken } from "@/utils/api";

const isUnsignedInError = (message: string) =>
  message.toLowerCase().includes("signed in") || message.toLowerCase().includes("sign in");

export const useSavedSshKeys = () => {
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");
  const [keys, setKeys] = useState<SavedSshKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setKeys([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const next = await listSshKeys();
      setKeys(next);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load SSH keys";
      setKeys([]);
      setError(isUnsignedInError(message) ? null : message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, isLoggedIn]);

  const addKey = useCallback(async (input: CreateSshKeyInput) => {
    const created = await createSshKey(input);
    setKeys((prev) => [created, ...prev]);
    return created;
  }, []);

  const removeKey = useCallback(async (id: string) => {
    await deleteSshKey(id);
    setKeys((prev) => prev.filter((key) => key.id !== id));
  }, []);

  return {
    keys,
    isLoading,
    error,
    refresh,
    addKey,
    removeKey
  };
};
